import { isEmpty, isObjectLike, isBoolean, map } from 'lodash';

import * as DataOrigins from 'commons-vehicle/constants/dataOrigins.js';
import {
    COLLECTION_CASH_PURCHASE,
    MODEL_CASH_PURCHASE,
    VEHICLE_CASH_PURCHASE,
    REBATE_DETAILS,
} from 'commons-vehicle/PurchaseMethods/constants/dataConversions.js';
import { CASH_PURCHASE } from 'commons-vehicle/PurchaseMethods/constants/purchaseMethods.js';
import CashPurchaseModel from 'commons-vehicle/PurchaseMethods/models/CashPurchaseModel.js';
/* eslint import/prefer-default-export: 0 */

export function getRebatesDetailsFromData(rebatesDetailsData) {
    return map(rebatesDetailsData, (rebatesDetail) => {
        return {
            typeId: rebatesDetail[REBATE_DETAILS.REBATE_TYPE_ID],
            code: rebatesDetail[REBATE_DETAILS.REBATE_CODE],
            description: rebatesDetail[REBATE_DETAILS.REBATE_DESCRIPTION],
            dueAtDelivery: rebatesDetail[REBATE_DETAILS.REBATE_IS_AT_DELIVERY],
            isAfterTaxes: rebatesDetail[REBATE_DETAILS.REBATE_IS_AFTER_TAXES],
            amount: rebatesDetail[REBATE_DETAILS.REBATE_AMOUNT],
            taxes: rebatesDetail[REBATE_DETAILS.REBATE_TAXES] || 0,
            taxesAtDelivery: rebatesDetail[REBATE_DETAILS.REBATE_TAXES_AT_DELIVERY] || 0,
        };
    });
}

function resetCashPurchaseFromCollectionData(collectionCashPurchaseData, selected) {
    if (
        !collectionCashPurchaseData[COLLECTION_CASH_PURCHASE.PRICING] ||
        !collectionCashPurchaseData[COLLECTION_CASH_PURCHASE.PRICING][COLLECTION_CASH_PURCHASE.INDEX]
    ) {
        return new CashPurchaseModel();
    }

    const pricingIndex = collectionCashPurchaseData[COLLECTION_CASH_PURCHASE.PRICING][COLLECTION_CASH_PURCHASE.INDEX];
    const pricingAmounts = pricingIndex[COLLECTION_CASH_PURCHASE.AMOUNTS]
        ? {
              msrp: pricingIndex[COLLECTION_CASH_PURCHASE.AMOUNTS][COLLECTION_CASH_PURCHASE.MSRP],
              freightPDI: pricingIndex[COLLECTION_CASH_PURCHASE.AMOUNTS][COLLECTION_CASH_PURCHASE.FREIGHT_PDI],
              taxesAndDuties: pricingIndex[COLLECTION_CASH_PURCHASE.AMOUNTS][COLLECTION_CASH_PURCHASE.TAXES_AND_DUTIES],
          }
        : {
              msrp: pricingIndex[COLLECTION_CASH_PURCHASE.RETAIL],
              freightPDI: 0,
              taxesAndDuties: 0,
          };

    const rebatesDetailsData = isEmpty(pricingIndex[COLLECTION_CASH_PURCHASE.REBATES_DETAILS])
        ? []
        : pricingIndex[COLLECTION_CASH_PURCHASE.REBATES_DETAILS];
    const rebatesDetails = getRebatesDetailsFromData(rebatesDetailsData);

    return new CashPurchaseModel({
        purchaseMethod: CASH_PURCHASE,
        selected,
        available: false,
        retail: pricingIndex[COLLECTION_CASH_PURCHASE.RETAIL],
        discounts: pricingIndex[COLLECTION_CASH_PURCHASE.DISCOUNTS]
            ? pricingIndex[COLLECTION_CASH_PURCHASE.DISCOUNTS]
            : 0,
        rebates: pricingIndex[COLLECTION_CASH_PURCHASE.REBATES],
        rebatesTaxes: pricingIndex[COLLECTION_CASH_PURCHASE.REBATES_TAXES] || 0,
        rebatesAtDelivery: pricingIndex[COLLECTION_CASH_PURCHASE.DISCOUNTS]
            ? pricingIndex[COLLECTION_CASH_PURCHASE.REBATES_AT_DELIVERY]
            : 0,
        pricingAmounts,
        rebatesDetails,
    });
}

function resetCashPurchaseFromCalculatorData(calculatorCashPurchaseData, selected) {
    if (!calculatorCashPurchaseData.vehicle) {
        return new CashPurchaseModel();
    }

    let retail = calculatorCashPurchaseData.vehicle.sellingPrice;
    let retailAdjustment = 0;
    if (!isEmpty(calculatorCashPurchaseData.vehicle.price)) {
        const retailWithFees = calculatorCashPurchaseData.vehicle.price[MODEL_CASH_PURCHASE.RETAIL];
        retailAdjustment = retailWithFees - retail;
        retail = retailWithFees;
    }

    return new CashPurchaseModel({
        purchaseMethod: CASH_PURCHASE,
        selected,
        available: false,
        retail,
        retailAdjustment,
        discounts: 0,
        rebates: 0,
        rebatesTaxes: 0,
        rebatesAtDelivery: 0,
    });
}

function resetCashPurchaseFromModelData(modelCashPurchaseData, selected) {
    const cashPurchaseData = modelCashPurchaseData[MODEL_CASH_PURCHASE.PRICING]
        ? MODEL_CASH_PURCHASE.PRICING
        : VEHICLE_CASH_PURCHASE.PRICING;
    if (!modelCashPurchaseData[cashPurchaseData]) {
        return new CashPurchaseModel();
    }

    const includedItemsCodes = isEmpty(modelCashPurchaseData[cashPurchaseData][MODEL_CASH_PURCHASE.INCLUDED_CODES])
        ? []
        : modelCashPurchaseData[cashPurchaseData][MODEL_CASH_PURCHASE.INCLUDED_CODES];
    const rebatesDetailsData = isEmpty(modelCashPurchaseData[cashPurchaseData][MODEL_CASH_PURCHASE.REBATES_DETAILS])
        ? []
        : modelCashPurchaseData[cashPurchaseData][MODEL_CASH_PURCHASE.REBATES_DETAILS];
    const rebatesDetails = getRebatesDetailsFromData(rebatesDetailsData);

    const dealerFees = modelCashPurchaseData[cashPurchaseData][MODEL_CASH_PURCHASE.DEALER_FEES]
        ? modelCashPurchaseData[cashPurchaseData][MODEL_CASH_PURCHASE.DEALER_FEES]
        : 0;

    return new CashPurchaseModel({
        purchaseMethod: CASH_PURCHASE,
        selected,
        available: false,
        retail: modelCashPurchaseData[cashPurchaseData][MODEL_CASH_PURCHASE.RETAIL],
        dealerFees,
        discounts: modelCashPurchaseData[cashPurchaseData][MODEL_CASH_PURCHASE.DISCOUNTS],
        rebates: modelCashPurchaseData[cashPurchaseData][MODEL_CASH_PURCHASE.REBATES],
        rebatesTaxes: modelCashPurchaseData[cashPurchaseData][MODEL_CASH_PURCHASE.REBATES_TAXES] || 0,
        rebatesAtDelivery: modelCashPurchaseData[cashPurchaseData][MODEL_CASH_PURCHASE.REBATES_AT_DELIVERY],
        includedItemsCodes,
        rebatesDetails,
    });
}

export function resetCashPurchaseFromOriginData(dataOrigin, originCashPurchaseData, selected) {
    if (!isObjectLike(originCashPurchaseData) || isEmpty(originCashPurchaseData)) {
        return new CashPurchaseModel();
    }

    const updatedSelected = isBoolean(selected) ? selected : false;

    switch (dataOrigin) {
        case DataOrigins.MODELS_COLLECTION:
            return resetCashPurchaseFromCollectionData(originCashPurchaseData, updatedSelected);

        case DataOrigins.PAYMENT_CALCULATOR:
            return resetCashPurchaseFromCalculatorData(originCashPurchaseData, updatedSelected);

        case DataOrigins.MODEL_YEAR_POWERS_TRIMS:
            return resetCashPurchaseFromModelData(originCashPurchaseData, updatedSelected);

        case DataOrigins.VEHICLE_PAYMENTS:
            return resetCashPurchaseFromModelData(originCashPurchaseData, updatedSelected);

        default:
            return new CashPurchaseModel();
    }
}

export default {
    getRebatesDetailsFromData,
    resetCashPurchaseFromCollectionData,
    resetCashPurchaseFromCalculatorData,
    resetCashPurchaseFromModelData,
    resetCashPurchaseFromOriginData,
};
